import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface RatingGridProps {
  onRatingChange: (labManagementScore: number, interpersonalScore: number) => void;
  initialLabManagementScore?: number;
  initialInterpersonalScore?: number;
  readonly?: boolean;
}

const RatingGrid: React.FC<RatingGridProps> = ({
  onRatingChange,
  initialLabManagementScore = 50,
  initialInterpersonalScore = 50,
  readonly = false,
}) => {
  const [position, setPosition] = useState({
    x: initialLabManagementScore,
    y: 100 - initialInterpersonalScore,
  });

  const [isDragging, setIsDragging] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const getQuadrant = (x: number, y: number): string => {
    return x < 50
      ? y < 50 ? 'top-left' : 'bottom-left'
      : y < 50 ? 'top-right' : 'bottom-right';
  };

  const updatePosition = (clientX: number, clientY: number) => {
    if (!gridRef.current || readonly) return;

    const rect = gridRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(0, Math.round(((clientX - rect.left) / rect.width) * 100)), 100);
    const y = Math.min(Math.max(0, Math.round(((clientY - rect.top) / rect.height) * 100)), 100);
    
    setPosition({ x, y });
    
    const labManagementScore = x;
    const interpersonalScore = 100 - y;
    onRatingChange(labManagementScore, interpersonalScore);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (readonly) return;
    setIsDragging(true);
    updatePosition(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    updatePosition(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (readonly) return;
    setIsDragging(true);
    updatePosition(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    updatePosition(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto p-6 rounded-xl bg-gradient-to-br from-neutral-50 to-neutral-100 shadow-lg">
      {!readonly && (
        <div className="mb-6">
          <h3 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-neutral-900 to-neutral-600">Rate this PI</h3>
          <p className="mt-2 text-neutral-600">
            Click or drag the egg to rate the PI's interpersonal behavior and lab management.
          </p>
        </div>
      )}
      
      <div className="relative">
        <div className="relative pl-16 pr-4 pb-16" style={{ maxWidth: '500px' }}>
          <motion.div
            ref={gridRef}
            id="rating-grid"
            className="relative aspect-square bg-white border rounded-xl cursor-pointer shadow-lg backdrop-blur-sm bg-white/90 hover:shadow-xl transition-all duration-300"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'none' }}
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            {/* Grid lines with gradient */}
            <div className="absolute w-full h-full">
              <div className="absolute top-0 w-px h-full left-1/2 bg-gradient-to-b from-neutral-200/50 via-neutral-300/50 to-neutral-200/50"></div>
              <div className="absolute left-0 w-full h-px top-1/2 bg-gradient-to-r from-neutral-200/50 via-neutral-300/50 to-neutral-200/50"></div>
            </div>

            {/* Quadrant highlights with subtle animation */}
            <motion.div
              className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-primary-100/30 to-primary-50/30 rounded-tl-xl"
              animate={{ opacity: getQuadrant(position.x, position.y) === 'top-left' ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-primary-100/30 to-primary-50/30 rounded-tr-xl"
              animate={{ opacity: getQuadrant(position.x, position.y) === 'top-right' ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-primary-100/30 to-primary-50/30 rounded-bl-xl"
              animate={{ opacity: getQuadrant(position.x, position.y) === 'bottom-left' ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-primary-100/30 to-primary-50/30 rounded-br-xl"
              animate={{ opacity: getQuadrant(position.x, position.y) === 'bottom-right' ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />

            {/* Axis labels with enhanced styling */}
            <div className="absolute -left-2 top-0 h-full flex flex-col justify-between transform -translate-x-full px-4">
              <span className="text-sm font-medium text-neutral-800 bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Supportive Mentor</span>
              <span className="text-sm font-medium text-neutral-800 bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Negligent/Harmful</span>
            </div>
            <div className="absolute -left-2 top-1/2 transform -translate-x-full -translate-y-1/2 px-4">
              <span className="text-sm font-medium text-neutral-800 rotate-270 block bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Interpersonal Behavior</span>
            </div>

            <div className="absolute left-0 -bottom-2 w-full flex justify-between transform translate-y-full py-4">
              <span className="text-sm font-medium text-neutral-800 bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Toxic Environment</span>
              <span className="text-sm font-medium text-neutral-800 bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Healthy Environment</span>
            </div>
            <div className="absolute left-1/2 -bottom-2 transform -translate-x-1/2 translate-y-full py-4">
              <span className="text-sm font-medium text-neutral-800 bg-clip-text text-transparent bg-gradient-to-br from-neutral-900 to-neutral-600">Lab Management</span>
            </div>

            {/* Animated egg with enhanced effects */}
            <motion.div
              className="absolute cursor-grab active:cursor-grabbing"
              style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
                transform: 'translate(-50%, -50%)',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
              whileHover={{ scale: 1.1 }}
              whileDrag={{ scale: 1.15 }}
              drag
              dragConstraints={gridRef}
              dragElastic={0}
              dragMomentum={false}
              onDrag={(_, info) => {
                if (!gridRef.current || readonly) return;
                const rect = gridRef.current.getBoundingClientRect();
                const x = Math.min(Math.max(0, Math.round(((info.point.x - rect.left) / rect.width) * 100)), 100);
                const y = Math.min(Math.max(0, Math.round(((info.point.y - rect.top) / rect.height) * 100)), 100);
                setPosition({ x, y });
                onRatingChange(x, 100 - y);
              }}
            >
              <motion.span 
                className="text-3xl select-none filter drop-shadow-lg"
                animate={{ rotate: isDragging ? [0, -5, 5, -5, 5, 0] : 0 }}
                transition={{ duration: 0.5 }}
              >
                🥚
              </motion.span>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {!readonly && (
        <div className="mt-6 text-center">
          <motion.button
            onClick={() => {
              setPosition({ x: 50, y: 50 });
              onRatingChange(50, 50);
            }}
            className="px-6 py-2 text-sm font-medium text-neutral-700 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Reset Rating
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default RatingGrid; 